import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const signIn = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.two_factor_required) {
      // Hand back the ticket; UI completes login by POSTing to /auth/2fa/verify
      return { twoFactorRequired: true, ticket: data.ticket };
    }
    if (data.token) localStorage.setItem("auth_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completeTwoFactor = async (ticket, code) => {
    const { data } = await api.post("/auth/2fa/verify", { ticket, code });
    if (data.token) localStorage.setItem("auth_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const completePersonRegistration = (resp) => {
    if (resp.token) localStorage.setItem("auth_token", resp.token);
    setUser(resp.user);
  };

  const completeOrgRegistration = (resp) => {
    if (resp.token) localStorage.setItem("auth_token", resp.token);
    setUser({ ...resp.user, managed_org_name: resp.organization_name });
  };

  const signOut = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout API failure is non-fatal — we still clear local state.
      console.warn("[auth] logout API call failed:", err?.response?.status || err?.message);
    }
    localStorage.removeItem("auth_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signIn,
        completeTwoFactor,
        signOut,
        refreshUser,
        completePersonRegistration,
        completeOrgRegistration,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
